import { ReactElement, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LocalStorage from 'utils/bom-dom-manipulation/local-storage';
import { EIconSize } from 'utils/constants/app/ui';
import { MAIN_MARKETS_TYPE } from 'utils/constants/sportsbook/data-fields';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { MarketGroupItem } from 'interfaces/sportsbook-data-levels';
import { EventAction } from 'components/EventAction';
import { GlobalIcon } from 'components/GlobalIcon';
import { useOpenLoginPopup } from 'hooks/account/useOpenLoginPopup';
import { toggleFavorite } from 'store/actions';
import { getFavoriteActiveStateById } from 'store/selectors/fav-data';
import { getIsLoggedIn } from 'store/selectors/user-data';

type Props = {
  group?: MarketGroupItem;
  isMainMarket?: boolean;
  className?: string;
};

export const FavoriteButtonMarket = ({
  group,
  isMainMarket,
  className
}: Props): ReactElement => {
  const dispatch = useDispatch();
  const openLoginPopup = useOpenLoginPopup();
  const isLoggedIn = useSelector(getIsLoggedIn);

  const groupName = useMemo(() => {
    if (isMainMarket) {
      return MAIN_MARKETS_TYPE;
    }

    return `${group?.markets?.[0]?.type || ''}-${
      group?.markets?.[0]?.name || ''
    }`;
  }, [isMainMarket, group]);

  const active = useSelector(getFavoriteActiveStateById(groupName, 'markets'));

  const handleFavGroup = (e: UIEvent) => {
    e.stopPropagation();

    if (!isLoggedIn && SpringConfigs.USER_PERSONALIZATION_ENABLED) {
      LocalStorage.setItem(
        storageKeyName('account', 'UNSIGNED_FAV_MARKET'),
        groupName
      );
      openLoginPopup();
    } else {
      dispatch(toggleFavorite({ groupKey: 'markets', entity: groupName }));
    }
  };

  return (
    <EventAction
      size="md"
      ghost
      active={active}
      dataTestid={`market-favorite${active ? '-active' : ''}`}
      icon={
        <GlobalIcon
          lib="generic"
          name="favorite"
          theme="default"
          size={EIconSize._20}
        />
      }
      className={className}
      onClick={handleFavGroup}
    />
  );
};
